import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { crmAnalyticsApi, type DateRangeParams } from "api/crm-analytics";
import { getErrorMessage } from "utils/error";
import { Lead } from "views/leads-id/types";
import { AnalyticsDetailModal } from "./AnalyticsDetailModal";
import { PropertyGroupHeader } from "./PropertyGroupHeader";
import type { UnitFilterParams } from "./UnitsFilter";
import {
  DEFAULT_PER_PAGE,
  DRILLDOWN_ROWS_BASE_CLASSNAME,
  FOUR_COLS_DRILLDOWN_GRID_CLASSNAME
} from "../constants";
import { groupByProperty } from "../utils";
import { cx, formatPhone } from "shared/utils";

const COLUMNS = [
  { label: "Name" },
  { label: "Unit" },
  { label: "Source" },
  { label: "Created date", className: "text-right" }
];
const LeadRowContent = ({ lead }: { lead: Lead }) => (
  <>
    <div className="min-w-0">
      <p className="text-sm font-medium text-content-700 truncate">
        {lead.first_name} {lead.last_name}
      </p>
      <p className="text-sm text-content-700 truncate">
        {lead.primary_phone
          ? formatPhone(lead.primary_phone)
          : lead.primary_email}
      </p>
    </div>
    <p className="text-sm font-medium text-content-700 truncate">
      {lead.unit?.title ?? "--"}
    </p>
    <p className="text-sm font-medium text-content-700 truncate">
      {lead.source?.name ?? "--"}
    </p>
    <div className="text-right">
      <p className="text-sm font-medium text-content-700 whitespace-nowrap">
        {moment(lead.created_at).format("MMMM D, YYYY")}
      </p>
      <p className="text-sm text-content-700">
        {moment(lead.created_at).format("h:mm A")}
      </p>
    </div>
  </>
);
const LeadRow = ({ lead }: { lead: Lead }) => {
  if (lead.id === null)
    return (
      <div
        className={cx(
          DRILLDOWN_ROWS_BASE_CLASSNAME,
          FOUR_COLS_DRILLDOWN_GRID_CLASSNAME
        )}
      >
        <LeadRowContent lead={lead} />
      </div>
    );
  return (
    <Link
      to={`/leads/${lead.id}`}
      className={cx(
        FOUR_COLS_DRILLDOWN_GRID_CLASSNAME,
        DRILLDOWN_ROWS_BASE_CLASSNAME
      )}
    >
      <LeadRowContent lead={lead} />
    </Link>
  );
};

const selectLeadProperty = (lead: Lead) => ({
  id: lead.property?.id ?? null,
  name: lead.property?.name
});

export const LeadsCreatedModal = ({
  opened,
  onClose,
  dateParams,
  filterParams
}: {
  opened: boolean;
  onClose: () => void;
  dateParams: DateRangeParams;
  filterParams?: UnitFilterParams;
}) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (opened) setPage(1);
  }, [opened]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["CRM_ANALYTICS_LEADS", dateParams, page, filterParams],
    queryFn: () =>
      crmAnalyticsApi.getLeads({
        ...filterParams,
        ...dateParams,
        page,
        per_page: Number(DEFAULT_PER_PAGE)
      }),
    enabled: opened,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const leads = useMemo(() => data?.items ?? [], [data]);
  const totalPages = data?.page_count ?? 0;
  const pagination = useMemo(
    () => ({ total: totalPages, current: page, onChange: setPage }),
    [totalPages, page]
  );
  const groups = useMemo(
    () => groupByProperty(leads, selectLeadProperty),
    [leads]
  );
  return (
    <AnalyticsDetailModal
      opened={opened}
      onClose={onClose}
      title="Leads created"
      gridClassName={FOUR_COLS_DRILLDOWN_GRID_CLASSNAME}
      columns={COLUMNS}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={leads.length === 0}
      emptyText="No leads found"
      pagination={pagination}
    >
      {groups.map((group) => (
        <Fragment key={group.key}>
          <PropertyGroupHeader name={group.name} />
          {group.items.map((lead) => (
            <LeadRow key={lead.id} lead={lead} />
          ))}
        </Fragment>
      ))}
    </AnalyticsDetailModal>
  );
};
